import {
  Dispatch,
  SetStateAction,
  ReactNode,
  PropsWithChildren,
  useState,
  useEffect,
  useCallback,
  FC,
  createContext,
  useContext,
} from 'react'

import LoadPartnerError from '@legacy/components/Website/LoadPartnerError'
import usePrivateFetchPartner from '@legacy/hooks/api/private/useFetchPartner'
import useWebsiteFetchPartner from '@legacy/hooks/api/website/useFetchPartner'
import Partner from '@legacy/models/Partner'
import AppScopeStore, { AppScope } from '@legacy/utils/AppScopeStore'

export type SetPartnerIdCallback = Dispatch<SetStateAction<string | undefined | null>>

export type SetSelectedPartnerCallback = Dispatch<SetStateAction<Partner | undefined>>

type ContextType = {
  partner: Partner | undefined
  isFetching: boolean
  /*
   * updates the state of this context
   * */
  updatePartner: (newState: any) => void
  setPartnerId: SetPartnerIdCallback
  setSelectedPartner: SetSelectedPartnerCallback
}

const nullContext: ContextType = {
  partner: undefined,
  isFetching: false,
  updatePartner: (_arg) => {},
  setPartnerId: () => {},
  setSelectedPartner: () => {},
}

type ProviderProps = {
  id?: string | null
  children: ReactNode
}

interface FetchComponentProps {
  id?: string | null
  setSelectedPartner: Dispatch<SetStateAction<Partner | undefined>>
  setIsFetching: Dispatch<SetStateAction<boolean>>
}

/**
 * We can't conditionally render hooks, but we can conditionally render
 * components, so we're using specialised components based on whether the app is
 * review/website, or one of the authenticatable apps.
 */
const WebsiteFetchPartner: FC<PropsWithChildren<FetchComponentProps>> = ({
  id,
  setSelectedPartner,
  setIsFetching,
  children,
}) => {
  const { isFetching, error, fetchPartner } = useWebsiteFetchPartner(setSelectedPartner)

  useEffect(() => {
    if (id) {
      fetchPartner(id)
    }
  }, [fetchPartner, id])

  useEffect(() => {
    setIsFetching(isFetching)
  }, [isFetching, setIsFetching])

  return <>{error ? <LoadPartnerError error={error} /> : children}</>
}

const PrivateFetchPartner: FC<PropsWithChildren<FetchComponentProps>> = ({
  id,
  setSelectedPartner,
  setIsFetching,
  children,
}) => {
  const { isFetching, fetchPartner } = usePrivateFetchPartner(setSelectedPartner)

  useEffect(() => {
    if (id) {
      fetchPartner(id)
    }
  }, [fetchPartner, id])

  useEffect(() => {
    setIsFetching(isFetching)
  }, [isFetching, setIsFetching])

  return <>{children}</>
}

const PartnerContext = createContext<ContextType | undefined>(undefined)

function useSelectedPartner() {
  const context = useContext(PartnerContext)
  if (!context) {
    /**
     * This covers us when the hook is called in components that are not under
     * the provider. Returning a null context allows the code to run without
     * being able to get the partner
     */
    return nullContext
  }
  return context
}

const SelectedPartnerProvider = ({ id, children }: ProviderProps) => {
  const [selectedPartner, setSelectedPartner] = useState<Partner | undefined>()
  const [partnerId, setPartnerId] = useState(id)
  const [isFetching, setIsFetching] = useState(false)

  const updatePartner = useCallback((newState: any) => {
    setSelectedPartner((prevState) => ({
      ...prevState,
      ...newState,
    }))
  }, [])

  const FetchComponent = AppScopeStore.scopeIsOneOf([AppScope.REVIEW, AppScope.PRODUCT_SITE, AppScope.PROPOSAL])
    ? WebsiteFetchPartner
    : PrivateFetchPartner

  const value: ContextType = {
    partner: selectedPartner,
    isFetching,
    updatePartner,
    setPartnerId,
    setSelectedPartner,
  }

  return (
    <PartnerContext.Provider value={value}>
      <FetchComponent id={partnerId} setSelectedPartner={setSelectedPartner} setIsFetching={setIsFetching}>
        {children}
      </FetchComponent>
    </PartnerContext.Provider>
  )
}

export { SelectedPartnerProvider, useSelectedPartner }
